import { Injectable } from '@angular/core';
import {
  DailyLogOperationInstance,
  DailyLogType,
  DailyOperationLogRecord,
  DataForDay,
  StepSelectionMethod
} from '../../models/projects/operation.models';
import { HttpClient } from '@angular/common/http';
import { DateService } from '../date/date.service';
import { DbContext, DbTable } from '../data/dbcontext.service';
import { OperationService } from '../operations/operation/operation.service';
import { DateParserService } from './daily-log-time/date-parser-service';
import { PtLuxonDate } from '../../models/projects/date.models';
import { process, State } from '@progress/kendo-data-query';
import { SetupService } from '../setup/setup.service';
import { SetupChangeLogRecord } from '../../models/setup/setup.models';
import { TimeZoneService } from '../time-zone/time-zone.service';
import { CompleteDayService } from '../complete-day/complete-day.service';
import { NavHelperService } from '../nav/nav-helper.service';
import { CurrentDayService } from '../../services/date/current-day.service';
import { cloneDeep } from 'lodash';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { Dictionary } from '../../pages/shared/dictionary';
import { AppConfig } from '../../app/app.config';
import { DateTime, Settings } from 'luxon';

export type CompareFunction = (a: DailyOperationLogRecord, b: DailyOperationLogRecord) => number;

@Injectable()
export class DailyLogService {
  config = AppConfig.settings;
  private readonly dailyLogTable: DbTable;
  private readonly millisecondsInDay = 86400000;
  private readonly LOG_HISTORY_KEY: string = 'log_history_key';
  private state: State = {
    take: 500,
    skip: 0,
    filter: {
      logic: 'and',
      filters: [
        {field: 'archived', operator: 'eq', value: false}
      ]
    },
    sort: [
      {
        field: 'sortTime',
        dir: 'asc'
      },
      {
        field: 'customSort',
        dir: 'asc'
      },
      {
        field: 'operationInstanceId', // keeps rows of the same operation together
        dir: 'asc'
      },
      {
        field: 'ordinal',
        dir: 'asc'
      },
      {
        field: 'operationStartTimeSort',
        dir: 'asc'
      },
      {
        field: 'startTimeOffset',
        dir: 'desc'
      },
      {
        field: 'uniqueOrdinal',
        dir: 'asc'
      }
    ],
  };

  private conditionDictionary: Dictionary<CompareFunction>;

  constructor(
    private http: HttpClient,
    private operationService: OperationService,
    private dateParserService: DateParserService,
    private dateService: DateService,
    private dbContext: DbContext,
    private setupService: SetupService,
    private timeZoneService: TimeZoneService,
    private completeDayService: CompleteDayService,
    private navHelperService: NavHelperService,
    private currentDayService: CurrentDayService
  ) {
    this.dailyLogTable = this.dbContext.tables.find(t => t.name === 'DailyLogData');
  }

  getState(): State {
    return this.state;
  }

  setState(state: State) {
    this.state = state;
  }

  async getDailyOperationLogRecord(id: number): Promise<DailyOperationLogRecord> {
    let record = await this.dbContext.get(this.dailyLogTable, id);
    return record ? await this.deserializeDailyLogRecord(record) : null;
  }

  sortDailyLogRecords(dailyLogData: DailyOperationLogRecord[], state: State): GridDataResult {
    const result: GridDataResult = {
      data: [],
      total: dailyLogData.length
    };

    this.initialiseSortingDictionary();

    // rough outline:
    // first loop below will try to order the given rows by sortTime,
    // if both sort times are equal, then check any special sort conditions, if found, return, if none exist, continue with other state.sort items
    result.data = dailyLogData.sort((a, b) => {
      // make sure to use (a, b) => {} instead of function (a, b), or calls using 'this.' will not be in scope
      for (let i = 0; i < state.sort.length; i++) {
        const sort = state.sort[i];

        // check that both rows have the field (some could be null, in which case we skip to next sort field (or custom handler if i === 2))
        if (a[sort.field] !== null && b[sort.field] !== null) {
          const orderValue = this.compare(a[sort.field], b[sort.field], sort.dir);

          // if order is found, return it here, if orderValue is 0 continue with custom check + other state.sort conditions
          // i !== 2 is important, as we still want to check with customHandler even if we have a value from the operationInstanceId, sorting with the Id is just to better group
          // rows so that the sorting is more consistent
          if (orderValue !== 0 && i !== 2) {
            return orderValue;
          }
        }

        // i === 2 means we have already tried to order with sortTime, customSort, and operationInstanceId, and still no resolution, so we need to check for a custom ordering rule
        // if no custom rule is found (it returns 0), then we continue with the rest of the sort.fields
        if (i === 2 && (a.customSort == null || b.customSort == null)) {
          const customOrderValue = this.compareWithCustomHandler(a, b);

          if (customOrderValue && customOrderValue !== 0) {
            return customOrderValue;
          }
        }
      }
    });

    if (state.filter.filters && state.filter.filters.length > 0) {
      // @ts-ignore
      if (state.filter.filters.some(e => e.field === 'archived')) {
        result.data = result.data.filter(e => !e.archived);
      }
    }

    return result;
  }

  compareWithCustomHandler(a: DailyOperationLogRecord, b: DailyOperationLogRecord): number {
    const aId = a.typeId;
    const bId = b.typeId;

    let first: DailyOperationLogRecord = null;
    let second: DailyOperationLogRecord = null;
    let multiplier: number = 0;

    // use a multiplier to return a negative result if the two compared rows are in reverse order
    // (all comparison functions below only work when row types are in the correct order, if they are in the reverse order, * by -1 to
    // return the inverse sorting value)
    // also ensure that the lower row type id is always the first item ina comparison function
    if (aId < bId) {
      first = a;
      second = b;
      multiplier = 1;
    } else {
      first = b;
      second = a;
      multiplier = -1;
    }

    const key = first.type + '-' + second.type;
    const valueFunc = this.conditionDictionary[key] || this.defaultCompare;
    const customOrderValue = multiplier * valueFunc(first, second);

    return customOrderValue;
  }

  initialiseSortingDictionary() {
    // IMPORTANT: the row type with the lowest enum number (in DailyLogType) will always be the first argument passed into a function
    // when creating comparison functions, the first parameter passed to the function should correspond to the first type in the function name
    this.conditionDictionary = new Dictionary<CompareFunction>();
    this.conditionDictionary.add('operationStart-operationEnd', this.checkOperationStartWithOperationEnd);
    this.conditionDictionary.add('operationStart-transiting', this.checkOperationStartWithTransiting);
    this.conditionDictionary.add('operationStart-locationChange', this.checkOperationStartWithLocationChange);
    this.conditionDictionary.add('operationStart-comment', this.checkOperationStartStepWithComment);
    this.conditionDictionary.add('operationStart-continued', this.checkOperationStartWithContinued);
    this.conditionDictionary.add('operationStart-paused', this.checkOperationStartWithPaused);
    this.conditionDictionary.add('operationStart-projectChange', this.checkOperationStartWithSetupChange);
    this.conditionDictionary.add('operationStart-phaseChange', this.checkOperationStartWithSetupChange);
    this.conditionDictionary.add('operationStart-stageChange', this.checkOperationStartWithSetupChange);
    this.conditionDictionary.add('step-comment', this.checkOperationStartStepWithComment);
    this.conditionDictionary.add('step-cancel', this.checkStepWithCancel);
    this.conditionDictionary.add('step-projectChange', this.checkStepWithSetupChange);
    this.conditionDictionary.add('step-phaseChange', this.checkStepWithSetupChange);
    this.conditionDictionary.add('step-stageChange', this.checkStepWithSetupChange);
    this.conditionDictionary.add('step-locationChange', this.checkStepWithSetupChange);
    this.conditionDictionary.add('step-continued', this.checkStepWithContinued);
    this.conditionDictionary.add('comment-transiting', this.checkCommentWithTransiting);
    this.conditionDictionary.add('comment-cancel', this.checkCommentWithCancel);
    this.conditionDictionary.add('operationEnd-transiting', this.checkOperationEndWithTransiting);
    this.conditionDictionary.add('operationEnd-locationChange', this.checkOperationEndWithLocationChange);
    this.conditionDictionary.add('operationEnd-projectChange', this.checkOperationEndWithSetupChange);
    this.conditionDictionary.add('operationEnd-phaseChange', this.checkOperationEndWithSetupChange);
    this.conditionDictionary.add('operationEnd-stageChange', this.checkOperationEndWithSetupChange);
    this.conditionDictionary.add('operationEnd-continued', this.checkOperationEndWithContinued);
    this.conditionDictionary.add('operationEnd-resumed', this.checkOperationEndWithResumed);
    this.conditionDictionary.add('step-operationStart', this.checkStepWithOperationStart);
    this.conditionDictionary.add('step-operationEnd', this.checkStepWithOperationEnd);
    this.conditionDictionary.add('step-resumed', this.checkStepWithResumed);
    this.conditionDictionary.add('operationEnd-typeChangeStart', this.checkOperationEndWithTypeChange);
    this.conditionDictionary.add('operationEnd-typeChangeEnd', this.checkOperationEndWithTypeChange);
  }

  // when comparing, the return result will always be applied to rowA
  // e.g return -1 to make rowA appear above rowB, and vice versa, return 0 keeps the same ordering
  private defaultCompare: CompareFunction = (rowA: DailyOperationLogRecord, rowB: DailyOperationLogRecord): number => {
    // tz change records should always appear before others with the same time, normal records have the same sort time as the start time,
    // but tz change records should base the sort time off of the end time
    if (rowA.type === 'timeZone') {
      return -1;
    }

    if (rowB.type === 'timeZone') {
      return +1;
    }

    if (rowA.type === 'dateChange') {
      return +1;
    }

    if (rowB.type === 'dateChange') {
      return -1;
    }

    return 0;
  }

  private checkOperationStartWithOperationEnd: CompareFunction = (rowA: DailyOperationLogRecord, rowB: DailyOperationLogRecord): number => {
    if (this.rowOperationsEqual(rowA, rowB)) {
      return -1;
    } else {
      return +1;
    }
  }

  private checkOperationStartWithTransiting: CompareFunction = (rowA: DailyOperationLogRecord, rowB: DailyOperationLogRecord): number => {
    if (this.rowOperationsEqual(rowA, rowB)) {
      return +1;
    } else {
      return -1;
    }
  }

  private checkOperationStartWithLocationChange: CompareFunction = (rowA: DailyOperationLogRecord, rowB: DailyOperationLogRecord): number => {
    if (this.rowOperationsEqual(rowA, rowB)) {
      return -1;
    } else {
      return +1;
    }
  }

  private checkOperationStartWithSetupChange: CompareFunction = (rowA: DailyOperationLogRecord, rowB: DailyOperationLogRecord): number => {
    return +1;
  }

  private checkOperationEndWithLocationChange: CompareFunction = (rowA: DailyOperationLogRecord, rowB: DailyOperationLogRecord): number => {
    return -1;
  }

  private checkOperationEndWithTransiting: CompareFunction = (rowA: DailyOperationLogRecord, rowB: DailyOperationLogRecord): number => {
    if (this.rowOperationsEqual(rowA, rowB)) {
      return +1;
    } else {
      return -1;
    }
  }

  private checkOperationStartStepWithComment: CompareFunction = (rowA: DailyOperationLogRecord, rowB: DailyOperationLogRecord): number => {
    if (this.rowOperationsEqual(rowA, rowB)) {
      return -1;
    } else {
      return +1;
    }
  }

  private checkOperationStartWithContinued: CompareFunction = (rowA: DailyOperationLogRecord, rowB: DailyOperationLogRecord): number => {
    return +1;
  }

  private checkOperationStartWithPaused: CompareFunction = (rowA: DailyOperationLogRecord, rowB: DailyOperationLogRecord): number => {
    if (this.rowOperationsEqual(rowA, rowB)) {
      return -1;
    } else {
      return +1;
    }
  }

  private checkOperationEndWithResumed: CompareFunction = (rowA: DailyOperationLogRecord, rowB: DailyOperationLogRecord): number => {
    if (this.rowOperationsEqual(rowA, rowB)) {
      return +1;
    } else {
      return -1;
    }
  }

  private checkStepWithCancel: CompareFunction = (rowA: DailyOperationLogRecord, rowB: DailyOperationLogRecord): number => {
    if (this.rowOperationsEqual(rowA, rowB)) {
      return -1;
    } else {
      return +1;
    }
  }

  private checkStepWithSetupChange: CompareFunction = (rowA: DailyOperationLogRecord, rowB: DailyOperationLogRecord): number => {
    // step needs more context to know what to do here. If a stage change is logged with same time as final step and op end, then the step needs to come before
    // setup change but if the step in question is the first step in an operation that comes after the stage change, then it ofc needs to come after the stage change.
    if (rowA.operationStartTime.dateTime < rowB.sortTime) {
      return -1;
    } else {
      return +1;
    }
  }

  private checkStepWithContinued: CompareFunction = (rowA: DailyOperationLogRecord, rowB: DailyOperationLogRecord): number => {
    return +1;
  }

  private checkCommentWithTransiting: CompareFunction = (rowA: DailyOperationLogRecord, rowB: DailyOperationLogRecord): number => {
    if (this.rowOperationsEqual(rowA, rowB)) {
      return +1;
    } else {
      return -1;
    }
  }

  private checkCommentWithCancel: CompareFunction = (rowA: DailyOperationLogRecord, rowB: DailyOperationLogRecord): number => {
    if (this.rowOperationsEqual(rowA, rowB)) {
      return -1;
    } else {
      return +1;
    }
  }

  private checkStepWithOperationStart: CompareFunction = (rowA: DailyOperationLogRecord, rowB: DailyOperationLogRecord): number => {
    if (this.rowOperationsEqual(rowA, rowB)) {
      return +1;
    } else {
      return -1;
    }
  }

  private checkStepWithOperationEnd: CompareFunction = (rowA: DailyOperationLogRecord, rowB: DailyOperationLogRecord): number => {
    if (this.rowOperationsEqual(rowA, rowB)) {
      return -1;
    } else {
      return +1;
    }
  }

  private checkStepWithResumed: CompareFunction = (rowA: DailyOperationLogRecord, rowB: DailyOperationLogRecord): number => {
    if (this.rowOperationsEqual(rowA, rowB)) {
      return +1;
    } else {
      return -1;
    }
  }

  private checkOperationEndWithTypeChange: CompareFunction = (rowA: DailyOperationLogRecord, rowB: DailyOperationLogRecord): number => {
    if (this.rowOperationsEqual(rowA, rowB)) {
      return +1;
    } else {
      return -1;
    }
  }

  private checkOperationEndWithSetupChange: CompareFunction = (rowA: DailyOperationLogRecord, rowB: DailyOperationLogRecord): number => {
    return -1;
  }

  // checks operation api id and local id to see if two rows are part of the same instance
  rowOperationsEqual(rowA: DailyOperationLogRecord, rowB: DailyOperationLogRecord): boolean {
    if (rowA.operationInstanceId
      ? rowA.operationInstanceId === rowB.operationInstanceId
      : rowA.operationInstanceLocalId !== null && rowA.operationInstanceLocalId === rowB.operationInstanceLocalId) {
      return true;
    }

    // if (rowA.sortTime === rowB.operationStartTime.date) {
    //   return true;
    // }

    return false;
  }

  private checkOperationEndWithContinued: CompareFunction = (rowA: DailyOperationLogRecord, rowB: DailyOperationLogRecord): number => {
    return +1;
  }

  // for 'asc' direction
  // if less than 0, sort a to an index lower than b (i.e. a comes first).
  // if greater than 0, sort b to an index lower than a (i.e. b comes first).
  // if returns 0 items will not move
  // for 'desc' direction the opposite of the above applies
  compare(a, b, direction) {
    if (direction === 'asc') {
      if (a < b) {
        return -1;
      }
      if (b < a) {
        return 1;
      }
    } else if (direction === 'desc') {
      if (a < b) {
        return 1;
      }
      if (b < a) {
        return -1;
      }
    }

    return 0;
  }

  async getLogsForOperation(id: number): Promise<DailyOperationLogRecord[]> {
    const url = `${this.config.apiUrl}api/appdata/operationlogs/${id}`;

    const result = await this.http.get<DailyOperationLogRecord[]>(url).toPromise();
    const data = await this.deserializeDailyLogRecords(result);

    return data;
  }

  async getDailyLogRecord(
    operationId?: number,
    stepId?: number,
    type?: DailyLogType,
    localOperationId?: number): Promise<DailyOperationLogRecord> {
    const logs = await this.getDailyLogData();

    const linkedLog = logs.find(p =>
      (!!p.operationInstanceId
        ? p.operationInstanceId === operationId
        : localOperationId
          ? p.operationInstanceLocalId === localOperationId
          : false
          || !!p.stepInstance
            ? p.stepInstance.id === stepId
            : false)
      && !!p.typeId ? p.typeId === type : false);

    return linkedLog;
  }

  async getDailyLogRecordDirect(id: number): Promise<DailyOperationLogRecord> {
    return this.dbContext.get(this.dailyLogTable, id);
  }

  async addDailyLog(entry: DailyOperationLogRecord) {
    if (!!entry.operationInstanceId || !!entry.stepInstance) {
      let stepId = null;
      let operationId = null;

      if (!!entry.stepInstance) {
        stepId = entry.stepInstance.id;
      }

      if (!!entry.operationInstanceId) {
        operationId = entry.operationInstanceId;
      }

      const record = await this.getDailyLogRecord(operationId, stepId, entry.typeId);

      if (!!record) {
        entry.id = record.id;
      }
    }

    await this.saveDailyLogRecord(entry.id, entry);
  }

  async saveDailyLogRecord(id: number, dailyLogRecord: DailyOperationLogRecord) {
    const tempRecord = Object.assign({}, dailyLogRecord);
    // stop any grid row highlighting variables from being saved
    dailyLogRecord.selectedSwapHighlight = false;
    dailyLogRecord.selectedSwap = false;
    dailyLogRecord.selected = false;
    dailyLogRecord.disabled = false;

    dailyLogRecord = await this.dateParserService.removeOffsetFromLuxonDates(dailyLogRecord);
    await this.dbContext.addOrUpdate(this.dailyLogTable, <number>id, dailyLogRecord);

    // re-enable the grid row highlighting variables to their previous value
    dailyLogRecord.selectedSwapHighlight = tempRecord.selectedSwapHighlight;
    dailyLogRecord.selectedSwap = tempRecord.selectedSwap;
    dailyLogRecord.selected = tempRecord.selected;
    dailyLogRecord.disabled = tempRecord.disabled;

    return;
  }

  async getDailyLogDataForOperation(operationId: number, logData?: DailyOperationLogRecord[]) {
    if (!logData || logData.length === 0) {
      logData = await this.getDailyLogData();
    }
    const logDataClone = cloneDeep(logData) as DailyOperationLogRecord[];

    let operationLogData: DailyOperationLogRecord[] = [];
    if (operationId != null) {
      operationLogData = logDataClone.filter(
        p => p.operationInstanceId != null
          ? p.operationInstanceId === operationId
          : p.operationInstanceLocalId != null
            ? p.operationInstanceLocalId === operationId
            : false
      );
    }

    return operationLogData;
  }

  async getDailyLogData(): Promise<DailyOperationLogRecord[]> {
    let logData = await this.dbContext.getAll(this.dailyLogTable) as DailyOperationLogRecord[];

    logData.forEach(element => {
      if (element.sortTime == null) {
        element.sortTime = element.startTime && element.startTime.dateTime
          ? element.startTime.dateTime
          : element.endTime && element.endTime.dateTime
            ? element.endTime.dateTime
            : DateTime.local();
      }
    });

    logData = await this.deserializeDailyLogRecords(logData)

    return logData;
  }

  async getNonOperationalDailyLogData(ptLuxonDate: PtLuxonDate, dailyLogDataAll?: DailyOperationLogRecord[]): Promise<DailyOperationLogRecord[]> {
    let logs = await this.getExactDailyLogDataForDay(ptLuxonDate, dailyLogDataAll);
    logs = logs.filter(p => !p.operationInstanceId && !p.operationInstanceLocalId);
    return logs;
  }

  async getDailyLogDataForDay(date: PtLuxonDate): Promise<DataForDay> {
    const data = new DataForDay();
    const operations = await this.operationService.getAllProjectSpecificOperationInstancesForDay(date, null, null);

    let logData = [] as DailyOperationLogRecord[];

    const dailyLogDataAll = await this.getDailyLogData();

    for (const operation of operations) {
      let localLogData = operation.id
        ? await this.getDailyLogDataForOperation(operation.id, dailyLogDataAll)
        : await this.getDailyLogDataForOperation(operation.localId, dailyLogDataAll);

      logData = logData.concat(localLogData);
    }

    if (date) {
      const nonOperationalLogs = await this.getNonOperationalDailyLogData(date, dailyLogDataAll);
      logData = logData.concat(nonOperationalLogs);

      const endOfDay = await this.dateService.getEndOfDayLocally(date.dateTime);

      if (!endOfDay) {
        data.records = logData;
        return data;
      }

      data.startOfDay = date;
      data.endOfDay = endOfDay;
      data.previousDayData = logData.some(p => p.sortTime !== null && p.sortTime < date.dateTime);
      data.nextDayData = logData.some(p => this.logRecordRequiresNextDayData(p, logData, endOfDay.dateTime));

      const endOfNextDay = await this.dateService.getEndOfDayLocally(endOfDay.dateTime);

      data.continuingOperations = operations.filter(o => o.startTime.dateTime !== null &&
        o.startTime.dateTime < date.dateTime &&
        (o.cancelledTime === null || o.cancelledTime > date.dateTime)); // if op is cancelled then it should no longer be continued
      data.continuingSteps = [];

      logData = logData.filter(p => p.sortTime >= date.dateTime && (data.nextDayData ? p.sortTime < endOfNextDay.dateTime : p));
    }

    if (logData) {
      for (let i = 0; i < logData.length; i++) {
        // 'date' should still be unchanged since calling getCurrentLogDate in the daily log component, so we need to remove the local offset
        logData[i].disabled = !this.logInDay(logData[i], date.dateTime);
      }
    }

    data.records = logData;
    return data;
  }

  private logRecordRequiresNextDayData(
    record: DailyOperationLogRecord,
    logData: DailyOperationLogRecord[],
    endOfDay: DateTime
  ): boolean {
    if (!record || record.sortTime === null) {
      return false;
    }

    if (!this.isOperationalRecord(record)) {
      return record.sortTime >= endOfDay;
    }

    // A cancellation record is not, by itself, evidence that the operation continues.
    // Use the operation end/cancel time from the related records instead.
    if (record.typeId === DailyLogType.Cancel) {
      return false;
    }

    const effectiveEndTime = this.getEffectiveOperationEndTime(record, logData);
    if (effectiveEndTime) {
      return effectiveEndTime >= endOfDay;
    }

    // No end time and no cancel record means the operation is genuinely open-ended.
    return true;
  }

  private getEffectiveOperationEndTime(record: DailyOperationLogRecord, logData: DailyOperationLogRecord[]): DateTime | null {
    const operationEndTime = record.operationEndTime?.dateTime || null;

    const cancelRecord = logData.find(candidate =>
      candidate.typeId === DailyLogType.Cancel
      && this.recordsBelongToSameOperation(record, candidate)
      && candidate.sortTime !== null
    );

    const cancelTime = cancelRecord?.sortTime || null;

    if (operationEndTime && cancelTime) {
      return cancelTime < operationEndTime ? cancelTime : operationEndTime;
    }

    return operationEndTime || cancelTime;
  }

  private isOperationalRecord(record: DailyOperationLogRecord): boolean {
    return record.operationInstanceId != null || record.operationInstanceLocalId != null;
  }

  private recordsBelongToSameOperation(record: DailyOperationLogRecord, candidate: DailyOperationLogRecord): boolean {
    if (record.operationInstanceId != null && candidate.operationInstanceId != null) {
      return record.operationInstanceId === candidate.operationInstanceId;
    }

    if (record.operationInstanceLocalId != null && candidate.operationInstanceLocalId != null) {
      return record.operationInstanceLocalId === candidate.operationInstanceLocalId;
    }

    return false;
  }

  async getExactDailyLogDataForDay(ptLuxonDate: PtLuxonDate, logData?: DailyOperationLogRecord[]): Promise<DailyOperationLogRecord[]> {
    if (!ptLuxonDate || !ptLuxonDate.dateTime) {
      ptLuxonDate = new PtLuxonDate();
    }

    if (!logData) {
      logData = await this.getDailyLogData();
    }

    for (const log of logData) {
      // timeZone logs don't need the start offset changed
      if (!log.timeZoneId && log.startTime && log.startTime.dateTime) {
        log.startTimeOffset = log.startTime.dateTime.offset / 60;
      }

      if (!log.timeZoneId && log.endTime && log.endTime.dateTime) {
        log.endTimeOffset = log.endTime.dateTime.offset / 60;
      }
    }

    const dateTimeZone = await this.timeZoneService.getEditorLocally(ptLuxonDate);
    if (!dateTimeZone) {
      return [];
    }

    return logData
      .filter(p => p.startTime && p.startTime.dateTime
        ? this.dateService.compareDayOffsetsPreApplied(
          p.startTime.dateTime,
          ptLuxonDate.dateTime)
        : this.dateService.compareDayOffsetsPreApplied(
          p.endTime.dateTime,
          ptLuxonDate.dateTime));
  }

  async saveDailyLogData(dailyLogData: DailyOperationLogRecord[]): Promise<DailyOperationLogRecord[]> {
    for (const record of dailyLogData) {
      await this.saveDailyLogRecord(record.id ? record.id : record.localId, record);
    }

    return dailyLogData;
  }

  async deleteAllDailyLogData() {
    return await this.dbContext.removeAll(this.dailyLogTable);
  }

  async updatePreviousTransitingLocationChangeRecord(
    previousLocation: SetupChangeLogRecord,
    instance: DailyLogOperationInstance,
    uniqueOrdinal: number
  ): Promise<DailyOperationLogRecord> {
    let previousLocationChangeRecord = await this.getPreviousTransitingLocationChangeRecordByOperation(instance.id, instance.localId);

    if (!previousLocationChangeRecord) {
      previousLocationChangeRecord =
        await this.getPreviousTransitingLocationChangeRecordByDailyLogId(
          previousLocation.dailyLogRecordId,
          previousLocation.localDailyLogRecordId);
    }

    if (!!previousLocationChangeRecord) {
      // it might have been archived if the current instance was previously saved with no end time, so set false here to be safe
      previousLocationChangeRecord.archived = false;
      previousLocationChangeRecord.uniqueOrdinal = uniqueOrdinal;
      previousLocationChangeRecord.operationInstanceId = instance.id;
      previousLocationChangeRecord.operationInstanceLocalId = instance.localId;
      previousLocationChangeRecord.startTime = instance.endTime;
      previousLocationChangeRecord.sortTime = instance.endTime.dateTime;
      previousLocationChangeRecord.startTimeShow = instance.endTimeToShow;
      previousLocationChangeRecord.operationStartTime = instance.startTime;
      previousLocationChangeRecord.operationEndTime = instance.endTime;
      previousLocationChangeRecord.startTimeOffset = instance.endTimeOffset;

      return previousLocationChangeRecord;
    }

    return null;
  }

  /* Returns the previous operations Location Change record, if it exists */
  async getPreviousTransitingLocationChangeRecordByOperation(
    operationId: number,
    operationLocalId?: number): Promise<DailyOperationLogRecord> {
    const logData = await this.getDailyLogData();
    const gridData = process(logData, this.state);

    let index = gridData.data.findIndex(e => e.operationInstanceId && e.operationInstanceId === operationId);
    if (index === -1) {
      index = gridData.data.findIndex(e => e.operationInstanceId && e.operationInstanceId === operationLocalId);
    }

    for (let i = index; i > 0; i--) {
      if (gridData.data[i].operationInstanceId !== (operationId || operationLocalId)
        && gridData.data[i].operationClassification !== 'Rig Move'
        && gridData.data[i].operationClassification !== 'Delay'
        && gridData.data[i].operationClassification !== 'Interruption'
        && (gridData.data[i].type !== DailyLogType.LocationChange
          && gridData.data[i].operationInstanceId)
        && (gridData.data[i].type !== 'locationChange'
          && gridData.data[i].operationInstanceId)) {
        return null;
      }
      if ((gridData.data[i].type === DailyLogType.LocationChange || gridData.data[i].type === 'locationChange')
        && !!gridData.data[i].operationInstanceId) {
        return gridData.data[i];
      }
    }

    return null;
  }

  async getPreviousTransitingLocationChangeRecordByDailyLogId(dailyLogRecordId: number, localDailyLogRecordId: number): Promise<DailyOperationLogRecord> {
    const logData = await this.getDailyLogData();

    // create new state - normally we filter out archived records, if that filter is set to do so, then remove it for this method
    const stateWithArchivedRecords = cloneDeep(this.state);
    const archiveFilterIndex = stateWithArchivedRecords.filter.filters.findIndex(e => e.field === 'archived');
    stateWithArchivedRecords.filter.filters.splice(archiveFilterIndex, 1);

    const gridData = process(logData, stateWithArchivedRecords);

    let index = gridData.data.findIndex(e => e.id && e.id === dailyLogRecordId);
    if (index === -1) {
      index = gridData.data.findIndex(e => e.id && e.id === localDailyLogRecordId);
    }

    if (index !== -1) {
      return gridData.data[index];
    } else {
      return null;
    }
  }

  async getPreviousTransitingToRecordByTime(date: DateTime): Promise<DailyOperationLogRecord> {
    const logData = await this.getDailyLogData();

    // sort descending on time
    const transitingData = logData
      .filter(e => e.typeId === 10 && e.sortTime < date)
      .sort((function (a, b) {
        return b.sortTime.toMillis() - a.sortTime.toMillis();
      }));

    return transitingData[0];
  }

  async moveLocationChangeRecordToLastTransitOperation(
    transitRecord: DailyOperationLogRecord,
    locationChangeRecord: DailyOperationLogRecord
  ): Promise<DailyOperationLogRecord> {
    const logData = await this.getDailyLogData();

    const operationIds = logData
      .map(p => p.operationInstanceId ? p.operationInstanceId : p.operationInstanceId)
      .filter(p => p != null && p !== locationChangeRecord.operationInstanceId
        ? locationChangeRecord.operationInstanceId
        : locationChangeRecord.operationInstanceLocalId);

    let operations = await this.operationService.getProjectSpecificOperationInstances(operationIds);

    operations = operations.filter(e => e.projectSpecificOperation.operationSummary.classificationName === 'Rig Move');

    // sort descending
    operations = operations
      .sort((function (a, b) {
        return b.startTime.dateTime.toMillis() - a.startTime.dateTime.toMillis();
      }));

    const transitRecordSetupChange = await this.setupService.getSetupChangeLogRecord(transitRecord.setupChangeLogApiId
      ? transitRecord.setupChangeLogApiId
      : transitRecord.localSetupChangeLogRecordId);
    const currentLocationChangeSetupChange = await this.setupService.getSetupChangeLogRecord(locationChangeRecord.setupChangeLogApiId
      ? locationChangeRecord.setupChangeLogApiId
      : locationChangeRecord.localSetupChangeLogRecordId);

    locationChangeRecord.operationName = 'From ' + currentLocationChangeSetupChange.previousValue + ' ' + 'To ' + transitRecordSetupChange.newValue;
    locationChangeRecord.operationInstanceId = operations[0].id;
    locationChangeRecord.operationInstanceLocalId = operations[0].localId;
    locationChangeRecord.sortTime = operations[0].endTime.dateTime;
    locationChangeRecord.startTime.dateTime = operations[0].endTime.dateTime;
    locationChangeRecord.startTimeShow = this.dateService.setTimeStringFromPtLuxonDate(operations[0].endTime);
    locationChangeRecord.startTimeOffset = operations[0].endTimeOffset;

    return locationChangeRecord;
  }

  async mostRecentContractualOperation(ptLuxonDate: PtLuxonDate): Promise<DailyLogOperationInstance> {
    const logData = await this.getDailyLogDataForDay(ptLuxonDate);

    const operationIds = logData.records
      .map(p => p.operationInstanceId)
      .filter(p => p != null);

    let operations = await this.operationService.getProjectSpecificOperationInstances(operationIds);

    operations = operations.filter(p => !!p.startTime);

    if (operations.length < 1) {
      return null;
    }

    let currentTime = new PtLuxonDate(operations[0].startTime.dateTime);
    let currentOp = operations[0];

    operations.forEach(element => {
      if (element.startTime.dateTime > currentTime.dateTime) {
        currentTime = element.startTime;
        currentOp = element;
      }
    });

    return currentOp;
  }

  deleteDailyLogRecord(id: number) {
    if (!id) {
      return null;
    }

    const url = `${this.config.apiUrl}api/appdata/${id}/deletedailylogrecord`;

    return this.http.delete(url).toPromise()
      .then(res => {
        return Promise.resolve(res);
      }).catch(err => {
        return Promise.resolve(false);
      });
  }

  async deleteLocalDailyLogRecord(id: number) {
    if (!id) {
      return null;
    }

    await this.dbContext.remove(this.dailyLogTable, id);
  }

  getOperationStartTimeLog(logs: DailyOperationLogRecord[]): DailyOperationLogRecord {
    if (!logs) {
      return null;
    }

    return logs.find(p => p.typeId === DailyLogType.OperationStart);
  }

  getOperationTransitingToLog(logs: DailyOperationLogRecord[], id: number): DailyOperationLogRecord {
    if (!logs) {
      return null;
    }

    return logs.find(p => p.typeId === DailyLogType.Transiting && p.id === id);
  }

  getOperationCancelLog(logs: DailyOperationLogRecord[]): DailyOperationLogRecord {
    if (!logs) {
      return null;
    }

    return logs.find(p => p.typeId === DailyLogType.Cancel);
  }

  getOperationPauseLog(logs: DailyOperationLogRecord[], guid: string): DailyOperationLogRecord {
    if (!logs) {
      return null;
    }

    const pauseLogs = logs.filter(p => p.typeId === DailyLogType.Paused);

    return pauseLogs.find(p => p.secondaryGuid === guid);
  }

  getOperationResumeLog(logs: DailyOperationLogRecord[], guid: string): DailyOperationLogRecord {
    if (!logs) {
      return null;
    }

    const pauseLogs = logs.filter(p => p.typeId === DailyLogType.Resumed);

    const pauseLog = pauseLogs.find(p => p.secondaryGuid === guid);

    return pauseLog;
  }

  getOperationEndTimeLog(logs: DailyOperationLogRecord[]): DailyOperationLogRecord {
    if (!logs) {
      return null;
    }

    return logs.find(p => p.typeId === DailyLogType.OperationEnd);
  }

  getOperationStepLog(logs: DailyOperationLogRecord[], value: number, selectionMethod: StepSelectionMethod, group: number): DailyOperationLogRecord {
    if (!logs) {
      return null;
    }

    const stepLogs = logs.filter(p => p.typeId === DailyLogType.Step);

    switch (selectionMethod) {
      case StepSelectionMethod.id:
        return stepLogs.find(p => p.stepInstance.id === value);
      case StepSelectionMethod.stepNumber:
        return stepLogs.find(p => p.stepInstance.number === value && p.stepInstance.group === group);
    }

    return null;
  }

  getOperationSwitchLogStart(logs: DailyOperationLogRecord[], guid: string): DailyOperationLogRecord {
    if (!logs) {
      return null;
    }

    const stepLogs = logs.filter(p => p.typeId === DailyLogType.TypeChangeStart);

    const stepLog = stepLogs.find(p => p.secondaryGuid === guid);
    return stepLog;
  }

  getOperationSwitchLogEnd(logs: DailyOperationLogRecord[], guid: string): DailyOperationLogRecord {
    if (!logs) {
      return null;
    }

    const stepLogs = logs.filter(p => p.typeId === DailyLogType.TypeChangeEnd);

    const stepLog = stepLogs.find(p => p.secondaryGuid === guid);
    return stepLog;
  }

  getOperationCommentLog(logs: DailyOperationLogRecord[], identifier: number, id?: number): DailyOperationLogRecord {
    if (!logs) {
      return null;
    }

    const commentLogs = logs.filter(p => p.typeId === DailyLogType.Comment);
    let commentLog = commentLogs.find(p => p.commentIdentifier === identifier);

    if (id != null) {
      commentLog = commentLogs.find(p => p.id === id);
    }

    return commentLog;
  }

  getDateChangeLog(logs: DailyOperationLogRecord[]): DailyOperationLogRecord {
    if (!logs) {
      return null;
    }

    return logs.find(p => p.typeId === DailyLogType.DateChange);
  }

  async setDailyLogOperationData(record: DailyOperationLogRecord, operationInstance: DailyLogOperationInstance) {
    if (!record.operationInstanceId && !record.operationInstanceLocalId) {
      return;
    }
    switch (record.typeId) {
      case DailyLogType.OperationStart:
        operationInstance.startTime = cloneDeep(record.startTime);
        operationInstance.startTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(operationInstance.startTime);
        const startComment = operationInstance.startComment;

        if (!!startComment) {
          startComment.data = record.comment != null ? record.comment.data : null;
          startComment.appear = record.commentAppearOnDPR;
          startComment.milestone = record.comment != null ? record.comment.milestone : false;
        }

        operationInstance.startConfirmed = record.startTimeConfirmed;
        break;
      case DailyLogType.OperationEnd:
        operationInstance.endTime = cloneDeep(record.endTime);
        operationInstance.endTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(operationInstance.endTime);
        const endComment = operationInstance.endComment;

        if (!!endComment) {
          endComment.data = record.comment != null ? record.comment.data : null;
          endComment.appear = record.commentAppearOnDPR;
          endComment.milestone = record.comment != null ? record.comment.milestone : false;
        }

        operationInstance.endConfirmed = record.endTimeConfirmed;
        break;
      case DailyLogType.Step:
        const step = operationInstance.stepCollectionInstance.stepInstances.find(p => p.number === record.stepNumber);

        if (step == null) {
          break;
        }

        step.startTime = cloneDeep(record.startTime);
        step.endTime = cloneDeep(record.endTime);
        step.startTimeOffset = record.startTime && record.startTime.dateTime ? record.startTime.dateTime.offset / 60 : step.startTimeOffset;
        step.endTimeOffset = record.endTime && record.endTime.dateTime ? record.endTime.dateTime.offset / 60 : step.endTimeOffset;
        step.startTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(step.startTime);
        step.endTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(step.endTime);
        step.endConfirmed = record.endTimeConfirmed;
        step.startConfirmed = record.startTimeConfirmed;
        break;
      case DailyLogType.Comment:
        const comment = operationInstance.comments.find(p => p.identifier === record.commentIdentifier);

        if (comment && record.comment) {
          comment.data = record.comment != null ? record.comment.data : null;
          comment.appear = record.commentAppearOnDPR;
          comment.milestone = record.comment.milestone;
          comment.startTimeToShow = record.startTimeShow;
          comment.endTimeToShow = record.endTimeShow;
        }

        break;
    }
  }

  logInDay(record: DailyOperationLogRecord, dailyLogDateTime: DateTime): boolean {
    if (!dailyLogDateTime || isNaN(dailyLogDateTime.toMillis())) {
      return false;
    }

    record.startTime = PtLuxonDate.assignPtLuxonDate(record.startTime);
    record.endTime = PtLuxonDate.assignPtLuxonDate(record.endTime);

    if (!record.endTime.dateTime && record.typeId === DailyLogType.OperationEnd) {
      record.endTime = cloneDeep(record.sortTime);
    }

    const startExists = !!record.startTime.dateTime;
    const startEqual = this.dateService.compareDayOffsetsPreApplied(record.startTime.dateTime, dailyLogDateTime);

    if (startExists && !startEqual) {
      return false;
    }

    const endExists = !!record.endTime.dateTime;
    const endEqual = this.dateService.compareDayOffsetsPreApplied(record.endTime.dateTime, dailyLogDateTime);

    if (endExists && !endEqual) {
      return false;
    }

    return true;
  }

  async getCurrentLogDay(saveUpdatedDate: boolean = true, skipVerification?: boolean): Promise<PtLuxonDate> {
    // get latest date from local storage, if it doesn't exist, get it from the API instead (below)
    const isoDateString = await this.currentDayService.getCurrentLogDayFromLocalStorage();
    const ptLuxonDate = new PtLuxonDate();

    Settings.defaultZone = 'utc';

    if (!isoDateString) {
      ptLuxonDate.dateTime = await this.completeDayService.getLatestDate();

      // need to get the in-app timezone to get the correct localdate
      const tz = await this.timeZoneService.getEditor({dateTime: ptLuxonDate.dateTime, dateAmbiguous: false});

      // DateTime is set above from ISO string, so once we have the Tz we just need to add the offset
      ptLuxonDate.setOffset(tz.secondsOffsetFromUtc);

      if (saveUpdatedDate) {
        this.setCurrentLogDay(ptLuxonDate.dateTime);
      }

      return ptLuxonDate;
    } else {
      ptLuxonDate.dateTime = await this.dateParserService.deserializeDate(isoDateString, true, this.timeZoneService);

      if (!skipVerification) {
        // even if date is not null, we will still need to check with the api that it's still correct (as another user may have since logged a tz change,
        // or the user could just edit the date in local storage and potentially edit something they shouldn't be able to)
        ptLuxonDate.dateTime = await this.completeDayService.verifyDailyLogDate(ptLuxonDate.dateTime);
      }

      if (!ptLuxonDate.dateTime) {
        // if the verify returns null it means there is no completed days on the app, in this case just hit the getLatestDate() as this will return
        // the current api server time
        const latestDate = await this.completeDayService.getLatestDate() as DateTime;
        ptLuxonDate.dateTime = latestDate;
      }
    }

    if (saveUpdatedDate) {
      // dateTime is converted to UTC before being serialised, this is so that when the date is retrieved, we can be sure of the format,
      // therefore any time a date is de-serialised we then look up the in-app tz and apply it to the dateTime
      this.setCurrentLogDay(ptLuxonDate.dateTime);
    }

    return ptLuxonDate;
  }

  setCurrentLogDay(date: DateTime) {
    this.currentDayService.setCurrentLogDayInLocalStorage(date.toUTC().toISO());
  }

  public async deserializeDailyLogRecords(dailyLogRecords: DailyOperationLogRecord[]): Promise<DailyOperationLogRecord[]> {
    for (let i = 0; i < dailyLogRecords.length; i++) {
      dailyLogRecords[i] = await this.deserializeDailyLogRecord(dailyLogRecords[i]);
    }

    return dailyLogRecords;
  }

  public async deserializeDailyLogRecord(dailyLogRecord: DailyOperationLogRecord): Promise<DailyOperationLogRecord> {
    if (dailyLogRecord.startTime && dailyLogRecord.startTime.dateTime) {
      dailyLogRecord.startTime = await this.dateParserService.deserializePtLuxonDate(dailyLogRecord.startTime, this.timeZoneService, dailyLogRecord.startTimeOffset);
    }

    if (dailyLogRecord.endTime && dailyLogRecord.endTime.dateTime) {
      dailyLogRecord.endTime = await this.dateParserService.deserializePtLuxonDate(dailyLogRecord.endTime, this.timeZoneService, dailyLogRecord.endTimeOffset);
    }

    if (dailyLogRecord.operationStartTime && dailyLogRecord.operationStartTime.dateTime) {
      dailyLogRecord.operationStartTime = await this.dateParserService.deserializePtLuxonDate(dailyLogRecord.operationStartTime, this.timeZoneService);
      dailyLogRecord.operationStartTimeSort = dailyLogRecord.operationStartTime.dateTime;
    }

    if (dailyLogRecord.operationEndTime && dailyLogRecord.operationEndTime.dateTime) {
      dailyLogRecord.operationEndTime = await this.dateParserService.deserializePtLuxonDate(dailyLogRecord.operationEndTime, this.timeZoneService);
    }

    if (dailyLogRecord.sortTime) {
      dailyLogRecord.sortTime = await this.dateParserService.deserializeDate(dailyLogRecord.sortTime, true, this.timeZoneService);
    }

    if (dailyLogRecord.comment) {
      if (dailyLogRecord.comment.startTimeDate) {
        dailyLogRecord.comment.startTimeDate = await this.dateParserService.deserializePtLuxonDate(dailyLogRecord.comment.startTimeDate, this.timeZoneService, dailyLogRecord.comment.startTimeOffset);
      }

      if (dailyLogRecord.comment.endTimeDate) {
        dailyLogRecord.comment.endTimeDate = await this.dateParserService.deserializePtLuxonDate(dailyLogRecord.comment.endTimeDate, this.timeZoneService, dailyLogRecord.comment.endTimeOffset);
      }
    }

    if (dailyLogRecord.stepInstance) {
      if (dailyLogRecord.stepInstance.startTime) {
        dailyLogRecord.stepInstance.startTime = await this.dateParserService.deserializePtLuxonDate(dailyLogRecord.stepInstance.startTime, this.timeZoneService, dailyLogRecord.stepInstance.startTimeOffset);
      }

      if (dailyLogRecord.stepInstance.endTime) {
        dailyLogRecord.stepInstance.endTime = await this.dateParserService.deserializePtLuxonDate(dailyLogRecord.stepInstance.endTime, this.timeZoneService, dailyLogRecord.stepInstance.endTimeOffset);
      }
    }

    return dailyLogRecord;
  }

  async setReadingSortTimes(readings: DailyOperationLogRecord[]): Promise<DailyOperationLogRecord[]> {
    for (let i = 0; i < readings.length; i++) {
      readings[i] = await this.setTypes(readings[i]);
    }

    return readings;
  }

  async setTypes(reading: DailyOperationLogRecord): Promise<DailyOperationLogRecord> {
    if (!reading.type) {
      // reading.type is actually a string, but in lockedDay mode it may be undefined
      // @ts-ignore
      reading.type = DailyLogType[reading.typeId];
      // @ts-ignore
      reading.type = reading.type[0].toLowerCase() + reading.type.substring(1);
    }

    return reading;
  }

  public assignCustomSortValues(records: DailyOperationLogRecord[]) {
    let saveNeeded = false;

    for (let i = 0; i < records.length; i++) {
      if (records[i].id != null && records[i].customSort !== i) {
        records[i].customSort = i;
        saveNeeded = true;
      }
    }

    if (saveNeeded) {
      const recordsClone = this.dateParserService.removeOffsetFromLuxonDates(cloneDeep(records));
      const url = `${this.config.apiUrl}api/appdata/savesortvalues`;
      this.http.post<any>(url, recordsClone).toPromise().then();
    }
  }
}



// WEBPACK FOOTER //
// ./src/services/daily-log/daily-log.service.ts